import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { TrendingUp, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US')}`;

/**
 * FinancialImpactCard — calm, owner/GM-ready estimated savings widget.
 *
 * Reads from `/api/dashboard/financial-impact` (default period: this_week).
 * Defensive: shows an empty state when nothing has closed yet.
 */
export const FinancialImpactCard = ({ propertyId, period = 'this_week' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = localStorage.getItem('maris_token');
        const params = new URLSearchParams({ period });
        if (propertyId) params.set('propertyId', propertyId);
        const res = await axios.get(`${API}/dashboard/financial-impact?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mounted) setData(res.data);
      } catch {
        if (mounted) setData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [propertyId, period]);

  if (loading) {
    return (
      <Card className="border-stone-200">
        <CardContent className="p-5 flex items-center justify-center text-stone-400 text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading financial impact…
        </CardContent>
      </Card>
    );
  }

  const total = data?.totalEstimatedSavings || 0;
  const labor = data?.laborEfficiencySavings || 0;
  const preventative = data?.preventativeMaintenanceSavings || 0;
  const reputation = data?.reputationProtectionSavings || 0;
  const widgetLabel = data?.widgetLabel || 'This Week';

  const isEmpty = total === 0;

  return (
    <Card className="border-stone-200 overflow-hidden" data-testid="financial-impact-card">
      <CardContent className="p-0">
        {/* Headline band */}
        <div className="px-5 pt-5 pb-4 border-b border-stone-100">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-[10.5px] tracking-[0.18em] uppercase text-[#3F617E]">
                <TrendingUp className="h-3.5 w-3.5" />
                <span>Financial Impact</span>
              </div>
              <p className="text-[11.5px] text-stone-500 mt-1">
                Estimated Savings {widgetLabel}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/desk/reports')}
              className="text-[11px] text-[#3F617E] hover:text-[#345470] underline-offset-4 hover:underline"
              data-testid="financial-view-briefing"
            >
              View in briefing →
            </button>
          </div>
          {isEmpty ? (
            <p className="mt-3 text-[13px] text-stone-500 leading-relaxed">
              No financial impact calculated yet. Savings appear once tasks are closed.
            </p>
          ) : (
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-heading text-[34px] leading-none text-stone-900" data-testid="financial-total">
                {fmt(total)}
              </span>
              <span className="text-[11px] text-stone-400">estimated value protected</span>
            </div>
          )}
        </div>

        {/* Breakdown rows */}
        <div className="px-5 py-3 bg-stone-50/40 space-y-1.5">
          <ImpactRow label="Labor Efficiency"        amount={labor}        testId="financial-labor" />
          <ImpactRow label="Preventative Maintenance" amount={preventative} testId="financial-preventative" />
          <ImpactRow label="Reputation Protection"   amount={reputation}   testId="financial-reputation" />
        </div>

        {/* Caption */}
        <div className="px-5 py-3 border-t border-stone-100 text-[11.5px] text-stone-500 leading-relaxed">
          Based on handled tickets, fast resolutions, and recurring issues surfaced by Maris Desk.
        </div>
      </CardContent>
    </Card>
  );
};

const ImpactRow = ({ label, amount, testId }) => (
  <div className="flex items-center justify-between text-[13px]" data-testid={testId}>
    <span className="text-stone-600">{label}</span>
    <span className="font-medium text-stone-900 tabular-nums">{fmt(amount)}</span>
  </div>
);

export default FinancialImpactCard;
